"use client";
import Image from "next/image";
import Link from "next/link";
import { FaPinterestP } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import {
  FiChevronUp,
  FiFacebook,
  FiInstagram,
  FiLinkedin,
  FiMail,
  FiPhone,
} from "react-icons/fi";

interface Office {
  country: string;
  type: string;
  address: string;
}

// Country flag images are served from the public folder
const flagSrcMap: Record<string, string> = {
  India: "/icons/country-flags/india-flag.svg",
  USA: "/icons/country-flags/united-states-of-america.svg",
  Dubai: "/icons/country-flags/dubai-flag.svg",
  Canada: "/icons/country-flags/canada-flag.svg",
};

const Footer = () => {
  const officeAddresses = [
    { country: "USA", type: "Corporate Office", address: "5858 Horton Street, Suite 101, Emeryville, CA 94608" },
    { country: "Canada", type: "Corporate Office", address: "55 Village Centre Place Suite 307, Mississauga Ontario L4ZIV9, Canada" },
    { country: "Canada", type: "Corporate Office", address: "9328 Stuart Crescent, Surrey, BC, V3V 1T6, Canada" },
    { country: "Dubai", type: "Corporate Office", address: "Meydan Grandstand, 6th floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E" },
    { country: "India", type: "Development Office", address: "C-136, Industrial Area, Phase 8, Chandigarh, 160071" },
  ];
  const services = [
    {
      heading: "Design/Development-as-a-Service",
      href: "/design-development-as-a-service",
      links: [
        { href: "/ui-ux-design", label: "UI/UX Design" },
        { href: "/website-development", label: "Website Development" },
        { href: "/graphic-design", label: "Graphic Design" },
      ],
    },
    {
      heading: "Growth-as-a-Service",
      href: "/growth-as-a-service",
      links: [
        { href: "/organic-visibility", label: "Organic Visibility" },
        { href: "/performance-marketing", label: "Performance Marketing" },
        { href: "/content-marketing", label: "Content Marketing" },
      ],
    },



    {
      heading: "AI-as-a-Service",
      href: "/ai-as-a-service",
      links: [
        // { href: "/low-code-no-code-development", label: "LCNC Development/Solutions" },
      ],

    },

    {
      heading: "Staff-as-a-Service",
      href: "/staff-as-a-service",
      links: [],
    },
  ];

  const industries = [
    { href: "/industries/healthcare", label: "HealthCare" },
    { href: "/industries/real-estate", label: "Real Estate" },
    { href: "/industries/e-commerce", label: "E-commerce" },
    { href: "/industries/hospitality", label: "Hospitality" },
    { href: "/industries/ed-tech", label: "Edtech" },
    { href: "/technology-saas", label: "Technology/SaaS" },
    { href: "/logistics", label: "Logistics" },
  ];

  const resources = [
    { href: "/blogs", label: "Blog" },
    { href: "/thought-leadership", label: "Thought Leadership" },
    { href: "/news-pr", label: "News & PR" },
  ];

  const company = [
    { href: "/case-study", label: "Case Studies" },
    { href: "/testimonial", label: "Testimonials" },
    { href: "/about-us", label: "About Us" },
    { href: "/contact-us", label: "Contact Us" },
    { href: "/terms-conditions", label: "Terms & Conditions" },
    { href: "/privacy-policy", label: "Privacy Policy" },
    { href: "/sitemap", label: "Sitemap" },
  ];

  const socialLinks = [
    {
      href: "https://www.linkedin.com/company/makemybrandlabs",
      icon: FiLinkedin,
      label: "LinkedIn"
    },
    {
      href: "https://www.facebook.com/MakeMyBrandLabs/",
      icon: FiFacebook,
      label: "Facebook"
    },
    {
      href: "https://www.instagram.com/makemybrandlabs/",
      icon: FiInstagram,
      label: "Instagram"
    },
    {
      href: "https://www.pinterest.com/makemybrand_labs/",
      icon: FaPinterestP,
      label: "Pinterest"
    },
    {
      href: "https://x.com/MakeMyBrandLabs",
      icon: FaXTwitter, // ✅ Now using the official X logo
      label: "X"
    }
  ];

  return (
    <footer className="bg-[#272D57] text-white">
      {/* Scroll to top */}
      <div className="relative">
        <div className="absolute left-1/2 transform -translate-x-1/2 -top-3 sm:-top-5">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full border-2 border-primary bg-white text-[#252b56] hover:scale-105 transition-all duration-200 shadow-md"
            aria-label="Scroll to top"
          >
            <FiChevronUp size={24} className="sm:w-8 sm:h-8" />
          </button>
        </div>
      </div>

      {/* Top Section */}
      <div className="custom-container mx-auto px-4 sm:px-6 lg:px-8 2xl:px-24 lg:pt-8 pt-10 ">
        <div className="py-12 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-12 xl:grid-cols-12 gap-12">

          {/* Services */}
          <div className="col-span-2 lg:col-span-5 xl:col-span-5">
            <h3 className="font-semibold mb-4">Services</h3>
            <div className="space-y-4 grid grid-cols-2 gap-4">
              {services.map((group, i) => (
                <div key={i}>
                  <h4 className="font-semibold text-sm mb-2">
                    <Link href={group.href} className="hover:text-white">
                      {group.heading}
                    </Link>
                  </h4>
                  <ul className="space-y-1 text-gray-300">
                    {group.links.map((link) => (
                      <li key={link.label}>
                        <Link
                          href={link.href}
                          className="hover:text-white text-sm"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Industries */}
          <div className="lg:col-span-3 xl:col-span-3">
            <h3 className="font-semibold mb-4">Industries</h3>
            <ul className=" list-none space-y-1 text-gray-300">
              {industries.map((item) => (
                <li key={item.label}>
                  <Link href={item.href} className="hover:text-white text-sm">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="lg:col-span-3 xl:col-span-3">
            <h3 className="font-semibold mb-4">Resources</h3>
            <ul className="space-y-1 text-gray-300">
              {resources.map((item) => (
                <li key={item.label}>
                  <Link href={item.href} className="hover:text-white text-sm">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-1 xl:col-span-1">
            <h3 className="font-semibold mb-4">Company</h3>
            <ul className="space-y-1 text-gray-300">
              {company.map((item) => (
                <li key={item.label}>
                  <Link href={item.href} className="hover:text-white text-sm whitespace-nowrap">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>

      {/* offer-address */}
      <div className="bg-[#313866] py-6 ">
        <div className="custom-container mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:flex lg:flex-row gap-8 md:gap-12  justify-between">
            {officeAddresses.map((office: Office, index: number) => (
              <div
                key={index}
                className="flex-1 flex flex-col gap-2 rounded-lg min-w-0 max-w-full"
              >
                <div className="flex items-center gap-3">
                  {/* Country flag */}
                  {flagSrcMap[office.country] ? (
                    <span className="w-6 h-6 overflow-hidden flex items-center justify-center">
                      <Image
                        src={flagSrcMap[office.country]}
                        alt={`${office.country} flag`}
                        width={20}
                        height={20}
                        className="w-full h-full object-cover"
                        priority={false}
                      />
                    </span>
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-700">
                      {office.country?.slice(0, 2).toUpperCase() || "??"}
                    </span>
                  )}
                  <h4 className="text-foreground text-white font-medium truncate">
                    {office.type}
                  </h4>
                </div>
                <p className="text-default-600 text-white text-sm break-words">
                  {office.address}
                </p>
              </div>
            ))}

          </div>
        </div>
      </div>
      {/* Bottom bar */}
      <div className="bg-[#313866] py-8 border-t border-white/10">
        <div className="custom-container mx-auto px-4 flex flex-col 2xl:flex-row items-center md:justify-between gap-6 md:gap-4 text-center md:text-left">

          {/* Contact + social */}
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-4 w-full md:w-auto items-center justify-center">

            {/* Contact */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-sm text-gray-300 items-center justify-center">

              {/* Numbers */}
              <div className="flex flex-row items-center justify-center gap-4">
                <Link
                  href="tel:+917717300314"
                  className="hover:text-white flex items-center gap-2"
                >
                  <FiPhone className="w-4 h-4" /> +91-771-730-0314
                </Link>

                {/* Separator: horizontal on mobile, vertical on sm+ */}
                <span className="block h-px w-2 sm:w-12 bg-white/20 sm:h-6 sm:w-px sm:w-auto" />

                <Link
                  href="tel:+918410700003"
                  className="hover:text-white flex items-center gap-2"
                >
                  +91-841-070-0003
                </Link>
              </div>

              {/* Separator (only sm+) */}
              <span className="hidden sm:block h-6 w-px bg-white/20" />

              {/* Email */}
              <Link
                href="mailto:info@makemybrandlabs.com"
                className="hover:text-white flex items-center gap-2"
              >
                <FiMail className="w-4 h-4" /> info@makemybrandlabs.com
              </Link>
            </div>

            {/* Divider (desktop only) */}
            <span className="hidden md:block h-6 w-px bg-white/20" />

            {/* Social */}
            <div className="flex items-center gap-4 justify-center">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <Link
                    key={social.href}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-gray-300 transition"
                    aria-label={social.label}
                  >
                    <Icon size={20} />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Copyright */}
          <div className="text-sm text-gray-300 order-last lg:order-first w-full md:w-auto text-center md:text-left">
            © Copyright <span className="font-bold text-white">mmbdigitallabs.</span> All Rights Reserved
          </div>
        </div>
      </div>


    </footer>
  );
};

export default Footer;
